import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link2, Eye, Target } from 'lucide-react';
import { SplitWords, FadeSlideIn } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';

const STEPS = [
  {
    number: '01', title: 'Connecting',
    description: 'We get to the truth of what\'s actually happening — beneath the surface, beyond the presenting problem.',
    icon: Link2, testId: TEST_IDS.methodStep1,
  },
  {
    number: '02', title: 'Revealing',
    description: 'We surface the dynamics, patterns, and blind spots that are keeping you stuck.',
    icon: Eye, testId: TEST_IDS.methodStep2,
  },
  {
    number: '03', title: 'Landing',
    description: 'We create a clear path forward with tools and clarity that outlast our work together.',
    icon: Target, testId: TEST_IDS.methodStep3,
  },
];

function TiltCard({ children, className, testId }) {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({ x: (y - 0.5) * -8, y: (x - 0.5) * 8 });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <motion.div
      ref={cardRef}
      data-testid={testId}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transition: 'transform 0.15s ease-out',
        transformStyle: 'preserve-3d',
      }}
      whileHover={{ y: -8 }}
    >
      {children}
    </motion.div>
  );
}

export default function MethodSection() {
  return (
    <section id="method" data-testid={TEST_IDS.methodSection} className="bg-[#F7F5F2] py-24 md:py-32 px-6 md:px-12 relative">
      <div className="max-w-7xl mx-auto">
        <FadeSlideIn className="mb-16 md:mb-20">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-block h-px bg-[#C9A96E] mr-3 align-middle"
            />
            The Luminous Method
          </p>
          <h2
            data-testid={TEST_IDS.methodHeadline}
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#1B2A4A] mb-6 max-w-2xl"
          >
            <SplitWords>Three Conversations. One Breakthrough.</SplitWords>
          </h2>
          <p className="text-base md:text-lg text-[#2D2D2D] font-sans leading-relaxed max-w-xl">
            Most coaches skim the surface. I go deep — because that's where the real change happens.
          </p>
        </FadeSlideIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <TiltCard
                testId={step.testId}
                className="bg-[#E8E0D5] p-8 md:p-10 border-l-2 border-[#C9A96E] group hover:bg-[#1B2A4A] transition-colors duration-500 h-full"
              >
                <div className="flex items-center justify-between mb-8">
                  <motion.span
                    className="font-serif text-5xl md:text-6xl text-[#C9A96E]/25 leading-none group-hover:text-[#C9A96E]/50 transition-colors duration-500"
                    whileHover={{ scale: 1.1 }}
                  >
                    {step.number}
                  </motion.span>
                  <motion.div
                    whileHover={{ rotate: 90, scale: 1.2 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <step.icon className="w-6 h-6 text-[#C9A96E]" />
                  </motion.div>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1B2A4A] mb-4 group-hover:text-[#F7F5F2] transition-colors duration-500">
                  {step.title}
                </h3>
                <p className="text-base text-[#2D2D2D] font-sans leading-relaxed group-hover:text-[#F7F5F2]/80 transition-colors duration-500">
                  {step.description}
                </p>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
